// Treasury & Reserves — Bank Admin tab (Stablecore replacement / GENIUS Act compliance).
// Five sections: Reserve Overview / Composition / Tokenized Deposit Ledger / Stablecoin Liquidity / Attestations.

import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  ShieldCheck, AlertTriangle, Loader2, RefreshCw, Download, Plus, ArrowRightLeft,
  Vault, FileCheck2, DollarSign, Coins, Layers, Building2, ExternalLink,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2/admin/treasury`;

// ── Helpers ──

const fmtUSD = (n, opts = {}) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: opts.dec ?? 2,
    maximumFractionDigits: opts.dec ?? 2,
  })}`;
const fmtUSDCompact = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return fmtUSD(v);
};
const fmtRel = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

const CHAIN_LABELS = {
  ethereum: "Ethereum",
  base: "Base",
  solana: "Solana",
  polygon: "Polygon",
};

const Toast = ({ msg, kind = "success", onClose }) => {
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(onClose, 2400);
    return () => clearTimeout(t);
  }, [msg, onClose]);
  if (!msg) return null;
  const bg = kind === "success" ? "bg-emerald-600" : "bg-rose-600";
  return (
    <div className={`fixed bottom-6 right-6 ${bg} text-white px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2 z-50 text-sm`} data-testid="treasury-toast">
      <ShieldCheck className="w-4 h-4" />{msg}
    </div>
  );
};

// ─────────────────────────────────────────────────
// SECTION 1 — Reserve Overview Dashboard
// (Banner is THE primary examiner-facing element — must be loud, green, unmissable)
// ─────────────────────────────────────────────────

const KpiCard = ({ icon: Icon, label, value, sublabel, tint = "slate" }) => {
  const tints = {
    emerald: "bg-emerald-50 text-emerald-700",
    sky: "bg-sky-50 text-sky-700",
    purple: "bg-purple-50 text-purple-700",
    amber: "bg-amber-50 text-amber-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`treasury-kpi-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
        <span className={`p-1.5 rounded-lg ${tints[tint]}`}><Icon className="w-4 h-4" /></span>
      </div>
      <p className="text-2xl font-bold text-slate-900 tabular-nums">{value}</p>
      {sublabel && <p className="text-xs text-slate-500 mt-1">{sublabel}</p>}
    </div>
  );
};

const ReserveBanner = ({ banner }) => {
  // Three states: fully_reserved (green), warning (amber), breach (rose)
  const isOk = banner.status === "fully_reserved";
  const isWarn = banner.status === "warning";
  const colorMap = {
    fully_reserved: {
      ring: "ring-emerald-500/40",
      gradient: "from-emerald-600 via-emerald-500 to-emerald-600",
      iconBg: "bg-emerald-400/20",
      pulse: "bg-emerald-300",
    },
    warning: {
      ring: "ring-amber-500/40",
      gradient: "from-amber-600 via-amber-500 to-amber-600",
      iconBg: "bg-amber-400/20",
      pulse: "bg-amber-300",
    },
    breach: {
      ring: "ring-rose-500/40",
      gradient: "from-rose-600 via-rose-500 to-rose-600",
      iconBg: "bg-rose-400/20",
      pulse: "bg-rose-300",
    },
  }[banner.status] || {};

  return (
    <div
      className={`relative overflow-hidden rounded-2xl ring-4 ${colorMap.ring} bg-gradient-to-r ${colorMap.gradient} text-white shadow-lg`}
      data-testid="treasury-reserve-banner"
      data-status={banner.status}
    >
      {/* Decorative grid pattern */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative px-7 py-7 flex items-center gap-6">
        <div className={`relative w-20 h-20 rounded-2xl ${colorMap.iconBg} flex items-center justify-center backdrop-blur`}>
          {isOk ? <ShieldCheck className="w-12 h-12" strokeWidth={2.2} /> : <AlertTriangle className="w-12 h-12" strokeWidth={2.2} />}
          <span className={`absolute -top-1 -right-1 flex h-3 w-3`}>
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${colorMap.pulse} opacity-75`}></span>
            <span className={`relative inline-flex rounded-full h-3 w-3 ${colorMap.pulse}`}></span>
          </span>
        </div>

        <div className="flex-1">
          <div className="flex items-baseline gap-4 flex-wrap">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight" data-testid="treasury-banner-label">
              {banner.label}
            </h2>
            <span className="text-2xl sm:text-3xl font-bold opacity-90 tabular-nums" data-testid="treasury-banner-ratio">
              Ratio: {banner.ratio_display}
            </span>
          </div>
          <p className="text-sm sm:text-base opacity-95 mt-1.5 max-w-3xl">{banner.subtitle}</p>
        </div>

        <div className="hidden md:flex flex-col items-end gap-1.5 text-xs">
          <span className="px-3 py-1 rounded-full bg-white/20 backdrop-blur font-semibold uppercase tracking-wider">
            GENIUS Act §4(b)
          </span>
          <span className="opacity-80">1:1 reserve attestation</span>
          {!isOk && (
            <span className="opacity-90 text-[11px]">{isWarn ? "Watchlist" : "Examiner action required"}</span>
          )}
        </div>
      </div>
    </div>
  );
};

const OverviewSection = ({ data }) => {
  if (!data) return (
    <div className="space-y-4">
      <div className="h-32 rounded-2xl bg-slate-100 animate-pulse" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[0,1,2,3].map((i) => <div key={i} className="h-24 rounded-xl bg-slate-100 animate-pulse" />)}
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="treasury-overview-section">
      <ReserveBanner banner={data.banner} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={Vault}
          label="Total Reserves"
          value={fmtUSDCompact(data.kpis.total_reserves_usd)}
          sublabel={fmtUSD(data.kpis.total_reserves_usd)}
          tint="emerald"
        />
        <KpiCard
          icon={Coins}
          label="Tokenized Supply"
          value={fmtUSDCompact(data.kpis.tokenized_supply_usd)}
          sublabel="Outstanding (active)"
          tint="purple"
        />
        <KpiCard
          icon={Layers}
          label="Stablecoin Float"
          value={fmtUSDCompact(data.kpis.stablecoin_float_usd)}
          sublabel="Cross-chain inventory"
          tint="sky"
        />
        <KpiCard
          icon={FileCheck2}
          label="Last Attestation"
          value={data.last_attestation?.period_label || "—"}
          sublabel={data.last_attestation
            ? `${data.last_attestation.auditor} · ${data.last_attestation.status.toUpperCase()}`
            : "No attestations yet"}
          tint="amber"
        />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────
// SECTION 2 — Reserve Composition (stacked bar chart)
// ─────────────────────────────────────────────────

const CompositionSection = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    axios.get(`${API}/composition?days=${days}`).then((r) => setData(r.data));
  }, [days]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="treasury-composition-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Reserve Composition</h3>
          <p className="text-xs text-slate-500">Daily breakdown by asset class — cash, T-Bills, repo, custody</p>
        </div>
        <div className="flex bg-slate-100 rounded-lg p-1" data-testid="treasury-composition-period-toggle">
          {[7, 14, 30].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`treasury-composition-period-${d}d`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition ${days === d ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {!data ? (
        <div className="h-72 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : (
        <>
          <div className="h-72" data-testid="treasury-composition-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} />
                <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={(v) => fmtUSDCompact(v)} />
                <Tooltip formatter={(v) => fmtUSD(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {Object.entries(data.labels).map(([k, label]) => (
                  <Bar key={k} dataKey={k} stackId="reserves" fill={data.colors[k]} name={label} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5" data-testid="treasury-composition-breakdown">
            {data.breakdown.map((b) => (
              <div key={b.type} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3" data-testid={`treasury-composition-row-${b.type}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: b.color }} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{b.label}</p>
                    <p className="text-xs text-slate-500 truncate">{b.custodian}</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  <p className="text-sm font-bold text-slate-900 font-mono">{fmtUSDCompact(b.balance_usd)}</p>
                  <p className="text-[11px] text-emerald-600">{(b.yield_bps / 100).toFixed(2)}% yld</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────
// SECTION 3 — Tokenized Deposit Ledger
// ─────────────────────────────────────────────────

const TokenizedLedgerSection = ({ toast }) => {
  const [data, setData] = useState(null);
  const [showMintForm, setShowMintForm] = useState(false);
  const [showBurnForm, setShowBurnForm] = useState(false);
  const [form, setForm] = useState({ counterparty: "", amount: "", note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/tokenized-deposits`);
    setData(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (kind) => {
    if (!form.counterparty || !form.amount) {
      toast("Counterparty and amount are required", "error");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/tokenized-deposits/${kind}`, {
        counterparty: form.counterparty,
        amount: Number(form.amount),
        note: form.note,
      });
      toast(`${kind === "mint" ? "Minted" : "Burned"} ${fmtUSD(form.amount)} for ${form.counterparty}`);
      setForm({ counterparty: "", amount: "", note: "" });
      setShowMintForm(false);
      setShowBurnForm(false);
      await load();
    } catch (e) {
      toast(e.response?.data?.detail || `${kind} failed`, "error");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="bg-white rounded-xl border border-slate-200 p-5 h-48 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="treasury-tokenized-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Tokenized Deposit Ledger</h3>
          <p className="text-xs text-slate-500">Issuance overview by counterparty + mint / burn history</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setShowBurnForm(false); setShowMintForm(!showMintForm); }} data-testid="treasury-mint-toggle"
            className="px-3 py-1.5 text-xs font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Mint
          </button>
          <button onClick={() => { setShowMintForm(false); setShowBurnForm(!showBurnForm); }} data-testid="treasury-burn-toggle"
            className="px-3 py-1.5 text-xs font-semibold rounded-md bg-slate-900 text-white hover:bg-slate-800 flex items-center gap-1.5">
            Burn
          </button>
        </div>
      </div>

      {/* Issuance summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="bg-emerald-50 rounded-lg p-3" data-testid="treasury-td-summary-outstanding">
          <p className="text-[10px] uppercase font-semibold text-emerald-700">Outstanding</p>
          <p className="text-lg font-bold text-emerald-900 font-mono">{fmtUSDCompact(data.summary.total_outstanding_usd)}</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-[10px] uppercase font-semibold text-slate-600">Holders</p>
          <p className="text-lg font-bold text-slate-900">{data.summary.active_holders}</p>
        </div>
        <div className="bg-sky-50 rounded-lg p-3">
          <p className="text-[10px] uppercase font-semibold text-sky-700">Mint 24h</p>
          <p className="text-lg font-bold text-sky-900 font-mono">{fmtUSDCompact(data.summary.mint_24h_usd)}</p>
        </div>
        <div className="bg-rose-50 rounded-lg p-3">
          <p className="text-[10px] uppercase font-semibold text-rose-700">Burn 24h</p>
          <p className="text-lg font-bold text-rose-900 font-mono">{fmtUSDCompact(data.summary.burn_24h_usd)}</p>
        </div>
      </div>

      {/* Mint/Burn form */}
      {(showMintForm || showBurnForm) && (
        <div className={`rounded-lg p-4 mb-4 border ${showMintForm ? "bg-emerald-50/50 border-emerald-200" : "bg-slate-50 border-slate-200"}`} data-testid="treasury-tokenized-form">
          <h4 className="text-sm font-bold text-slate-900 mb-3">
            {showMintForm ? "New Mint (issue tokenized deposits)" : "New Burn (redeem tokenized deposits)"}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-600">Counterparty</label>
              <select
                value={form.counterparty}
                onChange={(e) => setForm({ ...form, counterparty: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
                data-testid="treasury-form-counterparty">
                <option value="">Select holder…</option>
                {data.holders.map((h) => (
                  <option key={h.id} value={h.counterparty}>{h.counterparty}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-600">Amount (USD)</label>
              <input type="number" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="1000000"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm font-mono"
                data-testid="treasury-form-amount" />
            </div>
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-600">Note (optional)</label>
              <input type="text" value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="ACH funding ref XYZ"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                data-testid="treasury-form-note" />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={() => submit(showMintForm ? "mint" : "burn")} disabled={saving}
              className={`px-4 py-2 text-xs font-bold rounded-md text-white disabled:opacity-50 ${showMintForm ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-900 hover:bg-slate-800"}`}
              data-testid="treasury-form-submit">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (showMintForm ? "Confirm Mint" : "Confirm Burn")}
            </button>
            <button onClick={() => { setShowMintForm(false); setShowBurnForm(false); setForm({ counterparty: "", amount: "", note: "" }); }}
              className="px-4 py-2 text-xs font-semibold rounded-md text-slate-600 hover:bg-slate-100"
              data-testid="treasury-form-cancel">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Holders table */}
      <h4 className="text-xs font-bold uppercase text-slate-500 mb-2 mt-2">Active Holders</h4>
      <div className="border border-slate-200 rounded-lg overflow-hidden mb-5">
        <table className="w-full text-sm" data-testid="treasury-td-holders-table">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2.5">Counterparty</th>
              <th className="text-right px-4 py-2.5">Outstanding</th>
              <th className="text-left px-4 py-2.5">First Issued</th>
              <th className="text-left px-4 py-2.5">Last Activity</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.holders.map((h) => (
              <tr key={h.id} className="hover:bg-slate-50" data-testid={`treasury-td-holder-${h.id}`}>
                <td className="px-4 py-3 font-medium text-slate-900 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400" />{h.counterparty}
                </td>
                <td className="px-4 py-3 text-right font-mono font-bold text-slate-900">{fmtUSDCompact(h.outstanding_usd)}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(h.first_issued).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtRel(h.last_activity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mint/Burn log */}
      <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">Recent Mint / Burn Events</h4>
      <div className="border border-slate-200 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
        <table className="w-full text-sm" data-testid="treasury-mintburn-log">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide sticky top-0">
            <tr>
              <th className="text-left px-4 py-2.5">Type</th>
              <th className="text-left px-4 py-2.5">Counterparty</th>
              <th className="text-right px-4 py-2.5">Amount</th>
              <th className="text-left px-4 py-2.5">Reference</th>
              <th className="text-left px-4 py-2.5">When</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.log.map((e) => (
              <tr key={e.id} className="hover:bg-slate-50" data-testid={`treasury-mintburn-row-${e.id}`}>
                <td className="px-4 py-2.5">
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${e.kind === "mint" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                    {e.kind}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-slate-700">{e.counterparty}</td>
                <td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-900">{fmtUSDCompact(e.amount)}</td>
                <td className="px-4 py-2.5 text-xs font-mono text-slate-500">{e.tx_reference}</td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{fmtRel(e.timestamp)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────
// SECTION 4 — Stablecoin Liquidity Position
// ─────────────────────────────────────────────────

const StablecoinSection = ({ toast }) => {
  const [data, setData] = useState(null);
  const [showRebalance, setShowRebalance] = useState(false);
  const [rb, setRb] = useState({ from_chain: "ethereum", to_chain: "base", token: "USDC", amount: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/stablecoin-inventory`);
    setData(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitRebalance = async () => {
    if (!rb.amount || Number(rb.amount) <= 0) {
      toast("Amount must be positive", "error");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/stablecoin-inventory/rebalance`, { ...rb, amount: Number(rb.amount) });
      toast(`Rebalanced ${fmtUSD(rb.amount)} ${rb.token}: ${rb.from_chain} → ${rb.to_chain}`);
      setShowRebalance(false);
      setRb({ from_chain: "ethereum", to_chain: "base", token: "USDC", amount: "" });
      await load();
    } catch (e) {
      toast(e.response?.data?.detail || "Rebalance failed", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="bg-white rounded-xl border border-slate-200 p-5 h-48 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="treasury-stablecoin-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Stablecoin Liquidity Position</h3>
          <p className="text-xs text-slate-500">Cross-chain inventory across {data.chains.length} chains × {data.tokens.length} tokens</p>
        </div>
        <button onClick={() => setShowRebalance(!showRebalance)} data-testid="treasury-rebalance-toggle"
          className="px-3 py-1.5 text-xs font-semibold rounded-md bg-purple-600 text-white hover:bg-purple-700 flex items-center gap-1.5">
          <ArrowRightLeft className="w-3.5 h-3.5" /> Rebalance
        </button>
      </div>

      {/* Total float */}
      <div className="flex items-baseline gap-3 mb-4">
        <span className="text-3xl font-bold text-slate-900 tabular-nums">{fmtUSDCompact(data.summary.total_float_usd)}</span>
        <span className="text-xs text-slate-500 uppercase font-semibold">total float</span>
      </div>

      {/* Suggestions */}
      {data.suggestions?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4" data-testid="treasury-rebalance-suggestions">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-xs font-bold text-amber-900 uppercase">Rebalance Suggestions</p>
              {data.suggestions.map((s) => (
                <div key={s.id} className="text-sm text-amber-900">
                  <span className="font-semibold">{s.recommended_action}</span> — {s.reason}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Rebalance form */}
      {showRebalance && (
        <div className="bg-purple-50/50 border border-purple-200 rounded-lg p-4 mb-4" data-testid="treasury-rebalance-form">
          <h4 className="text-sm font-bold text-slate-900 mb-3">Cross-Chain Rebalance</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-600">Token</label>
              <select value={rb.token} onChange={(e) => setRb({ ...rb, token: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
                data-testid="treasury-rb-token">
                {data.tokens.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-600">From</label>
              <select value={rb.from_chain} onChange={(e) => setRb({ ...rb, from_chain: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
                data-testid="treasury-rb-from">
                {data.chains.map((c) => <option key={c.chain} value={c.chain}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-600">To</label>
              <select value={rb.to_chain} onChange={(e) => setRb({ ...rb, to_chain: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
                data-testid="treasury-rb-to">
                {data.chains.map((c) => <option key={c.chain} value={c.chain}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-600">Amount (USD)</label>
              <input type="number" value={rb.amount}
                onChange={(e) => setRb({ ...rb, amount: e.target.value })}
                placeholder="1000000"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm font-mono"
                data-testid="treasury-rb-amount" />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={submitRebalance} disabled={saving}
              className="px-4 py-2 text-xs font-bold rounded-md bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
              data-testid="treasury-rb-submit">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Confirm Rebalance"}
            </button>
            <button onClick={() => setShowRebalance(false)}
              className="px-4 py-2 text-xs font-semibold rounded-md text-slate-600 hover:bg-slate-100"
              data-testid="treasury-rb-cancel">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Inventory matrix: chain × token */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="treasury-stablecoin-matrix">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2.5">Chain</th>
              {data.tokens.map((t) => (
                <th key={t} className="text-right px-4 py-2.5">{t}</th>
              ))}
              <th className="text-right px-4 py-2.5">Chain Total</th>
              <th className="text-right px-4 py-2.5">24h Tx</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.chains.map((c) => {
              const chainRow = data.matrix[c.chain] || {};
              const tx24h = data.tokens.reduce((acc, t) => acc + (chainRow[t]?.tx_count_24h || 0), 0);
              return (
                <tr key={c.chain} className="hover:bg-slate-50" data-testid={`treasury-stablecoin-row-${c.chain}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-purple-500" />{c.label}
                    </span>
                  </td>
                  {data.tokens.map((t) => {
                    const cell = chainRow[t];
                    return (
                      <td key={t} className="px-4 py-3 text-right font-mono">
                        {cell ? (
                          <span className={cell.balance_usd > 0 ? "text-slate-900 font-semibold" : "text-slate-300"}>
                            {fmtUSDCompact(cell.balance_usd)}
                          </span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-right font-mono font-bold text-slate-900">
                    {fmtUSDCompact(data.chain_totals[c.chain] || 0)}
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-slate-500">{tx24h.toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-slate-50 font-bold">
            <tr>
              <td className="px-4 py-2.5 text-xs uppercase text-slate-500">Token Totals</td>
              {data.tokens.map((t) => (
                <td key={t} className="px-4 py-2.5 text-right font-mono text-slate-900">
                  {fmtUSDCompact(data.token_totals[t] || 0)}
                </td>
              ))}
              <td className="px-4 py-2.5 text-right font-mono text-slate-900">{fmtUSDCompact(data.summary.total_float_usd)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────
// SECTION 5 — Attestation & Audit Trail
// ─────────────────────────────────────────────────

const AttestationSection = ({ toast }) => {
  const [rows, setRows] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/attestations`);
    setRows(r.data.attestations);
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/attestations/generate`, { auditor: "Deloitte LLP" });
      toast(`Attestation generated: ${r.data.attestation.report_id}`);
      await load();
    } catch (e) {
      toast(e.response?.data?.detail || "Generation failed", "error");
    } finally {
      setGenerating(false);
    }
  };

  const download = async (att) => {
    try {
      const r = await axios.get(`${API}/attestations/${att.report_id}/report`, { responseType: "text" });
      const blob = new Blob([r.data], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${att.report_id}.txt`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast("Download failed", "error");
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="treasury-attestation-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Attestation & Audit Trail</h3>
          <p className="text-xs text-slate-500">Independent auditor reports — proves 1:1 reserve coverage to bank examiners</p>
        </div>
        <button onClick={generate} disabled={generating} data-testid="treasury-att-generate"
          className="px-3 py-1.5 text-xs font-semibold rounded-md bg-[#007377] text-white hover:bg-[#005f63] disabled:opacity-50 flex items-center gap-1.5">
          {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileCheck2 className="w-3.5 h-3.5" />}
          Generate New
        </button>
      </div>

      {!rows ? (
        <div className="h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">No attestations yet. Generate one to start.</div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="treasury-attestation-table">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2.5">Period</th>
                <th className="text-left px-4 py-2.5">Auditor</th>
                <th className="text-right px-4 py-2.5">Reserve Ratio</th>
                <th className="text-right px-4 py-2.5">Liabilities</th>
                <th className="text-right px-4 py-2.5">Reserves</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Issued</th>
                <th className="text-right px-4 py-2.5">Report</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((a) => {
                const liab = a.tokenized_supply_usd + a.stablecoin_float_usd;
                return (
                  <tr key={a.id} className="hover:bg-slate-50" data-testid={`treasury-att-row-${a.id}`}>
                    <td className="px-4 py-3 font-semibold text-slate-900">{a.period_label}</td>
                    <td className="px-4 py-3 text-slate-700">{a.auditor}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-emerald-700">{a.reserve_ratio.toFixed(4)}x</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-700">{fmtUSDCompact(liab)}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-700">{fmtUSDCompact(a.total_reserves_usd)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${a.status === "passed" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                        {a.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{new Date(a.issued_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => download(a)} data-testid={`treasury-att-download-${a.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-[#007377] hover:underline">
                        <Download className="w-3.5 h-3.5" /> {a.report_id}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────

export const TreasuryReservesTab = () => {
  const [overview, setOverview] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [toastState, setToastState] = useState({ msg: "", kind: "success" });
  const toast = (msg, kind = "success") => setToastState({ msg, kind });

  const loadOverview = useCallback(async () => {
    const r = await axios.get(`${API}/overview`);
    setOverview(r.data);
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview, refreshKey]);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadOverview();
      setRefreshKey((k) => k + 1);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div data-testid="admin-treasury-reserves">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Treasury & Reserves</h2>
          <p className="text-sm text-slate-500">
            Reserve management for tokenized deposits and stablecoin float — GENIUS Act §4(b) compliance
          </p>
        </div>
        <button onClick={onRefresh} disabled={refreshing} data-testid="treasury-refresh-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="space-y-6">
        <OverviewSection data={overview} />
        <CompositionSection />
        <TokenizedLedgerSection toast={toast} />
        <StablecoinSection toast={toast} />
        <AttestationSection toast={toast} />
      </div>

      <Toast msg={toastState.msg} kind={toastState.kind} onClose={() => setToastState({ msg: "", kind: "success" })} />
    </div>
  );
};
